import { Locale } from '@/i18n/config'
import { session } from './storage'
import dsBridge from 'dsbridge'

export interface DSUserInfo {
  avatarurl: string
  uid: string
  nickname: string
  duid: string
  accesstoken: string
}

export interface DSDeviceInfo {
  language: Locale
  sysLanguage: string
  mac: string
  version: string
  deviceModel: string
  systemVersion: string
  os: string
  platform: string
  versionCode: string
}

/**
 * 判断当前平台
 */
export const isAndroid = (): boolean => /Android/i.test(navigator.userAgent)
export const isIOS = (): boolean => /iPhone|iPad|iPod/i.test(navigator.userAgent)

/**
 * 通用的 DSBridge 数据解析和缓存函数
 * @param methodName - DSBridge 方法名
 * @param storageKey - Session 存储的 key
 * @returns 解析后的数据对象
 */
const parseDSBridgeData = <T>(methodName: string, storageKey: string): Partial<T> => {
  console.log('dsBridge methodName', methodName)
  console.log('dsBridge storageKey', storageKey)

  if (!dsBridge || Object.keys(dsBridge).length === 0) {
    return {}
  }
  const raw: unknown = dsBridge?.call(methodName, '')
  let result: Partial<T> = {}

  try {
    // 场景1：原生返回字符串（JSON）
    if (typeof raw === 'string' && raw) {
      const obj = JSON.parse(raw) as { res?: T }
      result = obj?.res ?? {}
    }
    // 场景2：原生直接返回对象
    else if (raw && typeof raw === 'object') {
      const obj = raw as { res?: T }
      result = obj?.res ?? {}
    }
  } catch (error) {
    console.error(`解析 ${methodName} 数据失败:`, error)
  }

  console.log('dsBridge result', result)

  // 缓存到 session
  session.set(storageKey, result)
  return result
}

/**
 * 获取用户信息
 */
export const getUserInfoDS = (): Partial<DSUserInfo> => {
  return parseDSBridgeData<DSUserInfo>('getUserInfo', 'userInfo')
}

/**
 * 获取设备信息
 */
export const getDeviceInfoDS = (): Partial<DSDeviceInfo> => {
  return parseDSBridgeData<DSDeviceInfo>('getDeviceInfo', 'deviceInfo')
}

/**
 * 获取语言
 *
 */
export const getLanguageDS = async (): Promise<Locale> => {
  const result = (await getDeviceInfoDS()) ?? ({} as DSDeviceInfo)
  return (result?.language || 'en') as Locale
}

/**
 * 创建标准的 DSBridge 请求对象
 */
const createDSBridgeRequest = (type = 0) => ({
  code: 0,
  res: { type },
})

/**
 * 关闭整个页面，返回 App
 * iOS 需要传递 JSON 字符串，Android 直接传递对象
 */
export const closeWebViewPage = (): void => {
  console.log('关闭整个页面，返回 App', 'closeWebViewPage')
  try {
    if (!dsBridge) {
      return
    }
    const req = createDSBridgeRequest()
    const params = isIOS() ? JSON.stringify(req) : req
    dsBridge?.call('closeWebViewPage', params)
  } catch (error) {
    console.error('关闭 WebView 页面失败:', error)
  }
}

/**
 * 关闭半屏窗口
 */
export const closeWebview = (): void => {
  console.log('关闭半屏窗口', 'closeWebview')
  try {
    if (!dsBridge) {
      return
    }
    const req = createDSBridgeRequest()
    dsBridge?.call('closeWebview', JSON.stringify(req))
  } catch (error) {
    console.error('关闭 WebView 半屏窗口失败:', error)
  }
}

/**
 * 跳转到用户个人主页
 * @param uid - 用户 ID
 * @returns 空字符串表示成功，空对象表示失败
 */
export const jumpToUser = (uid: number | string): string | Record<string, never> => {
  try {
    // 参数验证：uid 必须大于 0
    const uidNum = typeof uid === 'string' ? Number(uid) : uid
    if (!uidNum || uidNum <= 0) {
      console.warn('jumpToUser: 无效的 uid', uid)
      return {}
    }

    const schema = `yayya://user/profile?uid=${uidNum}`

    if (isIOS()) {
      // iOS 平台：直接调用 jumpToUser 方法
      console.log('ios jumpToUser', uidNum)
      dsBridge?.call('jumpToUser', uidNum)
    } else {
      // Android 平台：使用 startBySchema 方法
      const req = {
        code: 0,
        res: schema,
      }
      console.log('android startBySchema', JSON.stringify(req))
      dsBridge?.call('startBySchema', JSON.stringify(req))
    }

    return ''
  } catch (error) {
    console.error('跳转到用户主页失败:', error)
    return {}
  }
}

/**
 * 通过 DSBridge 调用客户端 startBySchema，跳转到 App 内指定路由
 * @param route - 目标路由路径或 schema 字符串，由客户端解析并完成跳转
 */
export const jumpToAppRoute = (route: string): void => {
  if (!route?.trim()) {
    console.warn('jumpToAppRoute: route 为空')
    return
  }
  if (!dsBridge) {
    console.warn('jumpToAppRoute: dsBridge 不可用')
    return
  }
  try {
    const params = { code: 0, res: route.trim() }
    const payload = JSON.stringify(params)
    console.log('jumpToAppRoute', payload)
    dsBridge.call('startBySchema', payload)
  } catch (error) {
    console.error('jumpToAppRoute 失败:', error)
  }
}

/**
 * 复制文本到剪贴板
 * @param tips - 提示信息
 * @param text - 要复制的文本内容
 * @returns 空字符串表示成功，空对象表示失败
 */
export const copyNew = async (
  tips: string,
  text: string,
): Promise<string | Record<string, never>> => {
  try {
    // 参数验证：文本内容不能为空
    if (!text || text.trim() === '') {
      console.warn('copyNew: 文本内容为空')
      return {}
    }

    if (!dsBridge) {
      console.warn('copyNew: dsBridge 不可用')
      return {}
    }

    const req = {
      code: 0,
      msg: 'success',
      res: { type: 7, tips, text },
    }

    dsBridge.call('appShare', JSON.stringify(req))
    return ''
  } catch (error) {
    console.error('复制文本失败:', error)
    return {}
  }
}

/**
 * 调用原生分享功能（邀请分享）
 * @param source - 来源：'房间页' | '邀请页'
 * @returns 空字符串表示成功，空对象表示失败
 */
export const shareInvitation = async (
  source: 'room' | 'default',
): Promise<string | Record<string, never>> => {
  console.log('dsBridge方法：shareInvitation，来源：', source)
  try {
    if (!dsBridge) {
      console.warn('shareInvitation: dsBridge 不可用')
      return {}
    }

    const req = { source } // default-是邀请页的分享按钮，room-房间里的分享按钮

    dsBridge.call('shareBtn', JSON.stringify(req))
    return ''
  } catch (error) {
    console.error('分享失败:', error)
    return {}
  }
}

/**
 * 打开浏览器
 * @param targetUrl - 目标 URL
 */
export const openInBrowser = (targetUrl: string) => {
  const encodedUrl = encodeURIComponent(targetUrl)
  dsBridge.call('openSystemBrowser', encodedUrl)
}

/**
 * 打开第三方 app
 * @param opt - 第三方 app 的配置参数
 * @returns 返回解析后的数据对象，失败时返回空对象
 */
export const openInThirdApp = async (
  opt: Record<string, unknown>,
): Promise<Record<string, unknown>> => {
  try {
    if (!dsBridge) {
      console.warn('openThirdApp: dsBridge 不可用')
      return {}
    }

    const req = {
      code: 0,
      res: {
        ...opt,
      },
    }

    const data = dsBridge.call('openThirdApp', JSON.stringify(req))

    if (data && data !== '') {
      try {
        return JSON.parse(data as string)
      } catch (parseError) {
        console.error('解析 openThirdApp 返回数据失败:', parseError)
        return {}
      }
    }

    return {}
  } catch (error) {
    console.error('打开第三方 app 失败:', error)
    return {}
  }
}

/**
 * 解析响应数据（支持字符串和对象格式）
 */
const parseResponse = (response: any): any => {
  return typeof response === 'string' ? JSON.parse(response) : response
}

/**
 * 从 Android 响应中提取 URL 数组
 * Android 返回格式可能是: [{filePath:'',url:''}] 或 {urls: []} 或 {url: ''}
 */
const extractAndroidUrls = (result: any): string[] => {
  if (Array.isArray(result)) {
    return result.map((item: any) => item?.url || '').filter((url: string) => url !== '')
  }
  if (result?.urls && Array.isArray(result.urls)) {
    return result.urls
  }
  if (result?.url) {
    return [result.url]
  }
  return []
}

/**
 * 处理上传回调的统一错误处理
 */
const handleUploadCallback = (
  response: any,
  platform: 'iOS' | 'Android',
  callback?: (urls: string[]) => void,
): void => {
  console.log(`${platform} 上传图片回调`, response, typeof response)
  try {
    const result = parseResponse(response)
    const urls =
      platform === 'iOS' ? (Array.isArray(result) ? result : [result]) : extractAndroidUrls(result)
    callback?.(urls)
  } catch (error) {
    console.error('解析上传结果失败', error)
    callback?.([])
  }
}

/**
 * 上传图片 V2（解决 vivo 手机兼容问题）
 * @param option - 上传配置选项
 * @param callback - 上传完成回调函数，接收 URL 数组
 */
export const uploadImageV2 = async function (option: any, callback: any) {
  console.log('上传图片V2', option)
  try {
    const config = {
      maxCount: option.maxCount || 9,
      sizeType: option.sizeType || 'compressed',
      ...option,
    }

    const platform = isIOS() ? 'iOS' : 'Android'
    const callbackWrapper = (response: any) => handleUploadCallback(response, platform, callback)

    dsBridge.call('uploadImageV2', JSON.stringify(config), callbackWrapper)
  } catch (error) {
    console.error('上传图片V2失败', error)
    callback?.([])
  }
}
